// contexts/ThemeContext.jsx
import React, { createContext, useContext, useEffect, useState } from 'react';

/* Light / dark mode for the back office.

   Wrap the app once, inside AuthProvider (App.jsx):

     <AuthProvider>
       <ThemeProvider>
         <Router> … </Router>
       </ThemeProvider>
     </AuthProvider>

   The provider writes data-theme onto <html>, which is what
   theme.css keys off. Preference is stored per browser in
   localStorage under 'uiTheme' and falls back to the OS setting.
*/

const ThemeContext = createContext();

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
};

const STORAGE_KEY = 'uiTheme';

const systemPrefersDark = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-color-scheme: dark)').matches;

export const ThemeProvider = ({ children }) => {
  // 'light' | 'dark' | 'system'
  const [preference, setPreference] = useState(
    () => localStorage.getItem(STORAGE_KEY) || 'system'
  );
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  // Follow the OS while the preference is 'system'.
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e) => setSystemDark(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const theme = preference === 'system' ? (systemDark ? 'dark' : 'light') : preference;

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const setTheme = (next) => {
    setPreference(next);
    if (next === 'system') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, next);
  };

  const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark');

  return (
    <ThemeContext.Provider
      value={{ theme, preference, isDark: theme === 'dark', setTheme, toggleTheme }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
